import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Check, SkipForward, Plus, ToggleLeft, ToggleRight, Tag, Search, Lightbulb, PartyPopper } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { AppIcons } from '../constants/icons';

interface PlateTaggerModalProps {
  items: any[];           // Untagged plates
  allItems: any[];        // All plates (for re-tagging)
  userId: string;
  onClose: () => void;
  onUpdate: () => void;
}

// Common plate tag categories with suggestions
const TAG_CATEGORIES = {
  'Themes': [
    'floral', 'geometric', 'abstract', 'nature', 'animals', 'holiday', 
    'seasonal', 'celestial', 'ocean', 'tropical', 'vintage', 'modern',
    'romantic', 'gothic', 'kawaii', 'steampunk', 'boho', 'minimalist'
  ],
  'Holidays & Seasons': [
    'christmas', 'halloween', 'valentines', 'easter', 'thanksgiving',
    'new years', 'st patricks', 'fourth of july', 'fall', 'winter', 
    'spring', 'summer', 'birthday', 'wedding'
  ],
  'Nature & Animals': [
    'flowers', 'roses', 'leaves', 'trees', 'butterflies', 'birds',
    'cats', 'dogs', 'unicorns', 'dragons', 'fish', 'insects', 'owls',
    'feathers', 'paws', 'shells', 'starfish', 'coral'
  ],
  'Patterns': [
    'stripes', 'dots', 'plaid', 'chevron', 'damask', 'lace', 'paisley',
    'houndstooth', 'argyle', 'scales', 'marble', 'wood grain', 'animal print',
    'leopard', 'zebra', 'snake', 'camo'
  ],
  'Shapes & Elements': [
    'hearts', 'stars', 'moons', 'suns', 'circles', 'triangles', 'diamonds',
    'arrows', 'crosses', 'spirals', 'swirls', 'mandalas', 'dreamcatchers',
    'infinity', 'anchors', 'keys', 'crowns', 'skulls'
  ],
  'Nail Art Styles': [
    'full nail', 'accent', 'french tip', 'negative space', 'gradient friendly',
    'layering', 'reverse stamping', 'leadlight', 'decal', 'tiny images',
    'large images', 'full coverage'
  ],
  'Objects & Things': [
    'food', 'drinks', 'fashion', 'makeup', 'music', 'sports', 'travel',
    'paris', 'london', 'beach', 'mountains', 'coffee', 'wine', 'cake',
    'ice cream', 'fruit', 'candy', 'jewelry', 'shoes', 'bags'
  ],
  'Text & Symbols': [
    'words', 'quotes', 'letters', 'numbers', 'symbols', 'emojis',
    'cursive', 'script', 'block letters', 'graffiti'
  ]
};

const PlateTaggerModal: React.FC<PlateTaggerModalProps> = ({ items, allItems, userId, onClose, onUpdate }) => {
  const [showOnlyUntagged, setShowOnlyUntagged] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [tags, setTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>('Themes');

  // Filter items based on toggle
  const displayItems = showOnlyUntagged ? items : allItems;
  const currentItem = displayItems[currentIndex];
  const progress = displayItems.length > 0 ? ((currentIndex + 1) / displayItems.length) * 100 : 0;

  // Get the photo URL for current item
  const getPhotoUrl = () => {
    if (currentItem?.photos?.length > 0) {
      const defaultPhoto = currentItem.photos.find((p: any) => p.isDefault);
      return defaultPhoto?.url || currentItem.photos[0].url;
    }
    return null;
  };

  const photoUrl = getPhotoUrl();

  // Load existing tags when item changes
  useEffect(() => {
    if (currentItem?.imageTags && currentItem.imageTags.length > 0) {
      setTags(currentItem.imageTags);
    } else {
      setTags([]);
    }
    setSearchQuery('');
  }, [currentIndex, currentItem, showOnlyUntagged]);

  // Add a tag
  const addTag = (tag: string) => {
    const normalizedTag = tag.toLowerCase().trim();
    if (normalizedTag && !tags.includes(normalizedTag)) {
      setTags([...tags, normalizedTag]);
    }
  };

  // Remove a tag
  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  // Add custom tag
  const handleAddCustomTag = () => {
    if (customTag.trim()) {
      // Split by comma to allow multiple tags at once
      const newTags = customTag.split(',').map(t => t.toLowerCase().trim()).filter(t => t);
      newTags.forEach(tag => {
        if (!tags.includes(tag)) {
          setTags(prev => [...prev, tag]);
        }
      });
      setCustomTag('');
    }
  };

  // Save and move to next
  const handleSaveAndNext = async () => {
    if (tags.length === 0) {
      alert('Please add at least one tag!');
      return;
    }

    setSaving(true);
    try {
      const itemRef = doc(db, 'users', userId, 'items', currentItem.id);
      await updateDoc(itemRef, { imageTags: tags });
      
      if (currentIndex < displayItems.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error('Error saving tags:', error);
      alert('Error saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Skip current item
  const handleSkip = () => {
    if (currentIndex < displayItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      onClose();
    }
  };

  // Navigate
  const goToPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < displayItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // Clear all tags
  const handleClearAll = () => {
    setTags([]);
  };

  // Filter suggestions based on search
  const getFilteredSuggestions = () => {
    if (!searchQuery) return null;
    
    const query = searchQuery.toLowerCase();
    const results: string[] = [];
    
    Object.values(TAG_CATEGORIES).forEach(categoryTags => {
      categoryTags.forEach(tag => {
        if (tag.includes(query) && !tags.includes(tag) && !results.includes(tag)) {
          results.push(tag);
        }
      });
    });
    
    return results.slice(0, 12);
  };

  const filteredSuggestions = getFilteredSuggestions();

  // No items to tag
  if (!currentItem || displayItems.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="glass-modal p-8 text-center max-w-md">
          <PartyPopper className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2 font-heading text-glass">
            {showOnlyUntagged ? 'All Done!' : 'No Plates Found'}
          </h2>
          <p className="text-glass-secondary mb-4 font-body">
            {showOnlyUntagged
              ? 'All of your plates have been tagged!'
              : 'No plates with photos found.'}
          </p>
          {showOnlyUntagged && allItems.length > 0 && (
            <button
              onClick={() => {
                setShowOnlyUntagged(false);
                setCurrentIndex(0);
              }}
              className="px-6 py-3 bg-purple-600/80 hover:bg-purple-600 text-white rounded-full font-bold transition-default mb-3 w-full font-body"
            >
              Re-tag Existing Plates
            </button>
          )}
          <button
            onClick={onClose}
            className="px-6 py-3 bg-slate-700 text-white rounded-full font-bold hover:bg-slate-600 transition-default w-full font-body"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tagger-overlay">
      {/* Header */}
      <div className="tagger-header">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="tagger-btn-close"
          >
            <X className="icon-base" />
          </button>
          <div>
            <h2 className="text-lg font-bold font-heading text-glass">Plate Tagger</h2>
            <p className="text-sm text-glass-secondary font-body">
              {currentIndex + 1} of {displayItems.length} plates
            </p>
          </div>
        </div>
        
        {/* Toggle: Show only untagged */}
        <button
          onClick={() => {
            setShowOnlyUntagged(!showOnlyUntagged);
            setCurrentIndex(0);
          }}
          className={`hidden sm:flex tagger-toggle font-body ${
            showOnlyUntagged ? 'tagger-toggle-untagged' : 'tagger-toggle-all'
          }`}
        >
          {showOnlyUntagged ? (
            <>
              <ToggleLeft className="icon-sm" />
              Untagged Only
            </>
          ) : (
            <>
              <ToggleRight className="icon-sm" />
              All Plates
            </>
          )}
        </button>

        {/* Progress bar */}
        <div className="flex-1 max-w-xs mx-4">
          <div className="tagger-progress-track">
            <div
              className="tagger-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrev}
            disabled={currentIndex === 0}
            className="tagger-btn-nav"
          >
            <ChevronLeft className="icon-base" />
          </button>
          <button
            onClick={goToNext}
            disabled={currentIndex === displayItems.length - 1}
            className="tagger-btn-nav"
          >
            <ChevronRight className="icon-base" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto p-4 flex flex-col lg:flex-row gap-4">
        {/* Left: Image */}
        <div className="tagger-card lg:w-1/2">
          {/* Item info */}
          <div className="text-center mb-3">
            <p className="text-purple-500 font-bold text-lg font-heading">
              {currentItem.brand}
            </p>
            <h3 className="text-xl font-bold text-glass font-body">
              {currentItem.name}
            </h3>
            {currentItem.plateId && (
              <p className="text-sm text-glass-secondary">Plate #{currentItem.plateId}</p>
            )}
          </div>

          {/* Image */}
          {photoUrl ? (
            <div className="flex justify-center">
              <img
                src={photoUrl}
                alt={currentItem.name}
                className="max-w-full max-h-[50vh] object-contain rounded-xl shadow-lg"
              />
            </div>
          ) : (
            <div className="w-full h-64 bg-gradient-to-br from-purple-400 to-indigo-600 rounded-xl flex items-center justify-center">
              <AppIcons.Plate className="w-24 h-24 text-glass-secondary" />
            </div>
          )}
        </div>

        {/* Right: Tags */}
        <div className="tagger-card lg:w-1/2 flex flex-col">
          {/* Current tags */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="tagger-section-header font-body">
                <Tag className="icon-sm" />
                Tags ({tags.length})
              </h4>
              {tags.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-xs text-glass-secondary hover:text-red-500"
                >
                  Clear all
                </button>
              )}
            </div>
            
            {tags.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-3 border-2 border-slate-500/50 rounded-xl min-h-[60px] bg-slate-800/30">
                {tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="tagger-tag"
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      className="ml-1 text-purple-200 hover:text-red-400"
                    >
                      <X className="icon-xs" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="tagger-empty">
                <p className="text-glass-secondary text-sm">No tags yet - add some below!</p>
              </div>
            )}
          </div>

          {/* Custom tag input */}
          <div className="mb-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-glass-secondary" />
                <input
                  type="text"
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddCustomTag();
                    }
                  }}
                  placeholder="Type tags (comma separated)..."
                  className="w-full pl-10 pr-4 py-2 border border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 text-sm font-body bg-slate-700/40 text-glass"
                />
              </div>
              <button
                onClick={handleAddCustomTag}
                disabled={!customTag.trim()}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg font-bold hover:bg-purple-600 transition-default disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="icon-md" />
              </button>
            </div>
          </div>

          {/* Search results */}
          {searchQuery && filteredSuggestions && filteredSuggestions.length > 0 && (
            <div className="mb-4 p-3 bg-slate-700/40 rounded-xl border border-slate-500/50">
              <p className="text-xs text-glass-secondary mb-2">Search results:</p>
              <div className="flex flex-wrap gap-1">
                {filteredSuggestions.map((tag, idx) => (
                  <button
                    key={idx}
                    onClick={() => addTag(tag)}
                    className="tagger-tag-suggestion"
                  >
                    + {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tag categories */}
          <div className="flex-1 overflow-y-auto mb-4">
            <p className="text-xs text-glass-secondary mb-2">Quick add by category:</p>
            <div className="space-y-2">
              {Object.entries(TAG_CATEGORIES).map(([category, categoryTags]) => (
                <div key={category} className="tagger-category">
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                    className="tagger-category-header"
                  >
                    {category}
                    <ChevronRight className={`w-4 h-4 transition-transform ${expandedCategory === category ? 'rotate-90' : ''}`} />
                  </button>
                  {expandedCategory === category && (
                    <div className="tagger-category-content">
                      {categoryTags.map((tag, idx) => {
                        const isSelected = tags.includes(tag);
                        return (
                          <button
                            key={idx}
                            onClick={() => isSelected ? removeTag(tag) : addTag(tag)}
                            className={isSelected ? 'tagger-tag-selected' : 'tagger-tag-suggestion'}
                          >
                            {isSelected ? '✓ ' : '+ '}{tag}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Toggle for re-tagging (mobile) */}
          <button
            onClick={() => {
              setShowOnlyUntagged(!showOnlyUntagged);
              setCurrentIndex(0);
            }}
            className={`sm:hidden w-full mb-3 tagger-toggle justify-center font-body ${
              showOnlyUntagged ? 'tagger-toggle-untagged' : 'tagger-toggle-all'
            }`}
          >
            {showOnlyUntagged ? (
              <>
                <ToggleLeft className="icon-sm" />
                Showing Untagged Only
              </>
            ) : (
              <>
                <ToggleRight className="icon-sm" />
                Showing All Plates
              </>
            )}
          </button>

          {/* Actions */}
          <div className="space-y-2">
            <button
              onClick={handleSaveAndNext}
              disabled={saving || tags.length === 0}
              className="tagger-btn-save font-body"
            >
              {saving ? (
                <>Saving...</>
              ) : currentIndex < displayItems.length - 1 ? (
                <>
                  <Check className="icon-md" />
                  Save {tags.length} Tag{tags.length !== 1 ? 's' : ''} & Next
                </>
              ) : (
                <>
                  <Check className="icon-md" />
                  Save & Finish
                </>
              )}
            </button>
            <button
              onClick={handleSkip}
              className="tagger-btn-skip font-body"
            >
              <SkipForward className="icon-md" />
              Skip for now
            </button>
          </div>

          {/* Tip */}
          <div className="tagger-tip mt-3">
            <p className="text-xs font-body flex items-center justify-center gap-2">
              <Lightbulb className="icon-sm" /> Tip: Type multiple tags separated by commas!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlateTaggerModal;
